import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/atoms';
import { FormField } from '../../components/molecues/FormField';
import { useMovieActions } from '../../hooks/useMovies';
import { getLanguageService } from '../../service/ServiceRegistry';
import { getGenreService } from '../../service/ServiceRegistry';
import type { Language } from '../../types/api/language';
import type { Genre } from '../../types/api/genre';
import type { CreateMovieRequest } from '../../types/api/movie';
import toast from 'react-hot-toast';
import { DurationHmsField } from '../../components/molecues/DurationHmsField';
import { secondsToRuntimeMinutes } from '../../utils/durationInput';
import { LoadingSpinner } from '../../components/atoms';

/**
 * MovieCreatePage Component
 * 
 * Separate page for creating movies with form-data upload support
 * Features:
 * - Form with all movie fields
 * - Thumbnail file upload
 * - Language dropdown selection
 * - Genre multi-select
 * - Validation
 * - Error handling
 */
export const MovieCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { createMovie } = useMovieActions();
  const languageService = getLanguageService();
  const genreService = getGenreService();

  const [formData, setFormData] = useState<CreateMovieRequest>({
    title: '',
    description: '',
    languageId: '',
    director: '',
    runtime: 120,
    releaseDate: '',
    initialGenreIds: [],
    primaryGenreId: '',
  });

  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  /** Total seconds from H:M:S; API receives `runtime` as rounded whole minutes. */
  const [runtimeTotalSeconds, setRuntimeTotalSeconds] = useState(120 * 60);
  
  // Options for dropdowns
  const [languages, setLanguages] = useState<Language[]>([]);
  const [genres, setGenres] = useState<Genre[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  // Load languages and genres
  useEffect(() => {
    const loadOptions = async () => {
      try {
        setLoadingOptions(true);
        const [langsResponse, genresResponse] = await Promise.all([
          languageService.getLanguages({ page: 1, size: 100 }),
          genreService.getPublicGenres({ page: 1, size: 100 }),
        ]);
        setLanguages(langsResponse.items);
        setGenres(genresResponse.items);
      } catch (error) {
        console.error('Failed to load options:', error);
        toast.error('Failed to load languages and genres');
      } finally {
        setLoadingOptions(false);
      }
    };
    loadOptions();
  }, [languageService, genreService]);

  // Handle thumbnail file selection
  const handleThumbnailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors((prev) => ({ ...prev, thumbnail: 'Please select an image file' }));
        return;
      }
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, thumbnail: 'File size must be less than 10MB' }));
        return;
      }
      setThumbnailFile(file);
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.thumbnail;
        return newErrors;
      });
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleChange = (field: keyof CreateMovieRequest, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleGenreToggle = (genreId: string) => {
    setFormData((prev) => {
      const currentIds = prev.initialGenreIds || [];
      const newIds = currentIds.includes(genreId)
        ? currentIds.filter((id) => id !== genreId)
        : [...currentIds, genreId];
      
      // If primary genre is removed, clear it
      const newPrimaryId = prev.primaryGenreId === genreId ? '' : prev.primaryGenreId;
      
      return {
        ...prev,
        initialGenreIds: newIds,
        primaryGenreId: newPrimaryId,
      };
    });
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.languageId) {
      newErrors.languageId = 'Language is required';
    }
    if (!formData.director.trim()) {
      newErrors.director = 'Director is required';
    }
    if (runtimeTotalSeconds <= 0) {
      newErrors.runtime = 'Runtime must be greater than zero';
    }
    if (!formData.releaseDate) {
      newErrors.releaseDate = 'Release date is required';
    }
    if (!formData.initialGenreIds || formData.initialGenreIds.length === 0) {
      newErrors.initialGenreIds = 'At least one genre is required';
    }
    if (!formData.primaryGenreId) {
      newErrors.primaryGenreId = 'Primary genre is required';
    }
    if (!thumbnailFile) {
      newErrors.thumbnail = 'Thumbnail image is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await createMovie(
        {
          ...formData,
          runtime: secondsToRuntimeMinutes(runtimeTotalSeconds),
        },
        thumbnailFile!
      );
      toast.success('Movie created successfully');
      navigate('/pages/movies');
    } catch (error: any) {
      toast.error(error.message || 'Failed to create movie');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingOptions) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-900">Create Movie</h1>
          <p className="text-gray-600 mt-1">Add a new movie to the platform</p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate('/pages/movies')}
        >
          Cancel
        </Button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h2 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2">
            Basic Information
          </h2>
          
          <FormField
            label="Title"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            error={errors.title}
            required
            placeholder="Enter movie title"
          />

          <div className="w-full">
            <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Enter movie description"
              rows={4}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
            />
            {errors.description && (
              <p className="text-sm text-red-600 mt-1.5">{errors.description}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="w-full">
              <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
                Language <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.languageId}
                onChange={(e) => handleChange('languageId', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
              >
                <option value="">Select a language</option>
                {languages
                  .filter((lang) => lang.isActive)
                  .map((lang) => (
                    <option key={lang.languageId} value={lang.languageId}>
                      {lang.name} ({lang.nameMm})
                    </option>
                  ))}
              </select>
              {errors.languageId && (
                <p className="text-sm text-red-600 mt-1.5">{errors.languageId}</p>
              )}
            </div>

            <FormField
              label="Director"
              value={formData.director}
              onChange={(e) => handleChange('director', e.target.value)}
              error={errors.director}
              required
              placeholder="Enter director name"
            />
          </div>

          <DurationHmsField
            label="Runtime"
            valueSeconds={runtimeTotalSeconds}
            onSecondsChange={(sec) => setRuntimeTotalSeconds(sec ?? 0)}
            required
            error={errors.runtime}
            helperText="Enter hours, minutes, and seconds. The API receives runtime as whole minutes (rounded from total seconds)."
            idPrefix="movie-create-runtime"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="Release Date"
              type="date"
              value={formData.releaseDate}
              onChange={(e) => handleChange('releaseDate', e.target.value)}
              error={errors.releaseDate}
              required
            />
          </div>
        </div>

        {/* Thumbnail Upload */}
        <div className="space-y-4">
          <h2 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2">
            Thumbnail
          </h2>
          
          <div className="w-full">
            <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
              Thumbnail Image <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleThumbnailChange}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
            />
            {errors.thumbnail && (
              <p className="text-sm text-red-600 mt-1.5">{errors.thumbnail}</p>
            )}
            <p className="text-sm text-gray-500 mt-1.5">
              JPEG, PNG, or WebP. Max 10MB. Will be auto-uploaded to storage.
            </p>
            
            {thumbnailPreview && (
              <div className="mt-4">
                <img
                  src={thumbnailPreview}
                  alt="Thumbnail preview"
                  className="w-48 h-64 object-cover rounded-lg border border-gray-300"
                />
              </div>
            )}
          </div>
        </div>

        {/* Genres */}
        <div className="space-y-4">
          <h2 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2">
            Genres
          </h2>
          
          <div className="w-full">
            <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
              Select Genres <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-4">
              {genres
                .filter((genre) => genre.isActive)
                .map((genre) => (
                  <label
                    key={genre.genreId}
                    className="flex items-center gap-2 cursor-pointer p-2 hover:bg-gray-50 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={formData.initialGenreIds?.includes(genre.genreId)}
                      onChange={() => handleGenreToggle(genre.genreId)}
                      className="w-4 h-4 text-[#EAB308] border-gray-300 rounded focus:ring-[#EAB308]"
                    />
                    <span className="text-sm text-gray-700">{genre.name}</span>
                  </label>
                ))}
            </div>
            {errors.initialGenreIds && (
              <p className="text-sm text-red-600 mt-1.5">{errors.initialGenreIds}</p>
            )}
          </div>

          <div className="w-full">
            <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
              Primary Genre <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.primaryGenreId}
              onChange={(e) => handleChange('primaryGenreId', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
            >
              <option value="">Select primary genre</option>
              {genres
                .filter((genre) => genre.isActive && formData.initialGenreIds?.includes(genre.genreId))
                .map((genre) => (
                  <option key={genre.genreId} value={genre.genreId}>
                    {genre.name}
                  </option>
                ))}
            </select>
            {errors.primaryGenreId && (
              <p className="text-sm text-red-600 mt-1.5">{errors.primaryGenreId}</p>
            )}
            {formData.initialGenreIds && formData.initialGenreIds.length > 0 && (
              <p className="text-sm text-gray-500 mt-1.5">
                Select from the genres you've chosen above
              </p>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/pages/movies')}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
          >
            Create Movie
          </Button>
        </div>
      </form>
    </div>
  );
};

export default MovieCreatePage;

